import { formatResponseRange, getBrandLogoUrl, getCategoryIconUrl, isWellKnownLogosOnly, } from '@/utils/helpers';
import { SPENDING_BREAKDOWN_PALETTE } from '@/composables/useSpendingAnalysis';
function fmtMoney(n, formatter) {
    const abs = Math.abs(n ?? 0);
    const formatted = formatter ? formatter.format(abs) : `£${abs.toFixed(2)}`;
    return n < 0 ? `-${formatted}` : formatted;
}
function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function loadImage(url) {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img.naturalWidth > 0 && img.naturalHeight > 0);
        img.onerror = () => resolve(false);
        img.src = url;
    });
}
export async function downloadSpendingReport(r, ctx) {
    const { filterTabs, recurringPayments, formatter, wellKnownLogosOnly, fallbackRangeLabel = '' } = ctx;
    const palette = SPENDING_BREAKDOWN_PALETTE;
    const parents = Array.isArray(r.parentCategories) ? r.parentCategories : [];
    const range = (r.fromDate && r.toDate) ? formatResponseRange(r.fromDate, r.toDate) : fallbackRangeLabel;
    const wkOnly = isWellKnownLogosOnly(wellKnownLogosOnly);
    const breakdown = parents.map((p, i) => {
        const isIncome = p.name === 'Income';
        const amount = isIncome ? (p.totalIn ?? 0) : ((p.totalOut ?? 0) - (p.totalIn ?? 0));
        const colour = filterTabs.find(t => t.label === p.name)?.color ?? palette[i % palette.length];
        return { name: p.name, amount, colour, isIncome };
    }).filter((b) => b.amount > 0);
    const totalIn = breakdown.filter((b) => b.isIncome).reduce((s, b) => s + b.amount, 0);
    const totalOut = breakdown.filter((b) => !b.isIncome).reduce((s, b) => s + b.amount, 0);
    const net = totalIn - totalOut;
    const maxBreakdown = Math.max(...breakdown.map((b) => b.amount), 1);
    const merchantMap = new Map();
    for (const p of parents) {
        const parentColour = filterTabs.find(t => t.label === p.name)?.color ?? p.color ?? palette[0];
        const merchants = Array.isArray(p.spendByMerchant) ? p.spendByMerchant : [];
        for (const m of merchants) {
            const mIn = m.totalIn ?? 0;
            const mOut = m.totalOut ?? 0;
            const amt = Math.abs(mOut > mIn ? mOut : mIn);
            if (amt <= 0)
                continue;
            const wellKnown = Array.isArray(m.transactions) && m.transactions.some((t) => t.wellKnownMerchant);
            const firstTx = m.transactions?.[0];
            const iconPath = firstTx?.icon ?? p.transactions?.[0]?.icon;
            const iconColour = firstTx?.color ?? p.transactions?.[0]?.color ?? p.color ?? parentColour;
            const subCategory = firstTx?.subClassification ?? firstTx?.classification ?? p.name;
            const existing = merchantMap.get(m.merchantName);
            if (existing) {
                existing.amount += amt;
                existing.count += m.transactionCount ?? 0;
                if (wellKnown)
                    existing.wellKnown = true;
                if (!existing.iconPath && iconPath)
                    existing.iconPath = iconPath;
                if (!existing.iconColour && iconColour)
                    existing.iconColour = iconColour;
            }
            else {
                merchantMap.set(m.merchantName, {
                    name: m.merchantName,
                    amount: amt,
                    count: m.transactionCount ?? 0,
                    colour: parentColour,
                    iconPath,
                    iconColour: iconColour,
                    wellKnown,
                    subCategory,
                    isIncome: mIn > mOut,
                });
            }
        }
    }
    const topMerchants = [...merchantMap.values()].sort((a, b) => b.amount - a.amount).slice(0, 10);
    const cats = Array.isArray(r.categories) ? r.categories : [];
    const topCategories = cats
        .map((c) => ({
        name: c.subCategory,
        parent: c.classification,
        amount: Math.abs(c.totalOut ?? 0),
        colour: c.categoryColor ?? c.color ?? c.transactions?.[0]?.color ?? palette[0],
        iconPath: c.iconPath ?? c.icon ?? c.transactions?.[0]?.icon,
    }))
        .filter((c) => c.amount > 0)
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 15);
    const slices = Array.isArray(r.timeSlicedBreakdowns) ? r.timeSlicedBreakdowns : [];
    const sliceMax = Math.max(...slices.map((s) => Math.abs(s.totalOut ?? 0) || Math.abs(s.net ?? 0)), 1);
    const recurring = recurringPayments ?? [];
    const generated = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    async function resolveMerchantIcon(m) {
        if (!wkOnly && m.isIncome) {
            return { url: require('@/assets/round1.png'), isLogo: false };
        }
        if (wkOnly && !m.wellKnown) {
            return { url: getCategoryIconUrl(m.iconPath), isLogo: false };
        }
        const logoUrl = getBrandLogoUrl(m.name);
        const ok = await loadImage(logoUrl);
        if (ok)
            return { url: logoUrl, isLogo: true };
        return { url: getCategoryIconUrl(m.iconPath), isLogo: false };
    }
    const resolvedMerchantIcons = await Promise.all(topMerchants.map(resolveMerchantIcon));
    function categoryIconHtml(iconPath, colour, size = 32) {
        const url = getCategoryIconUrl(iconPath);
        const iconSize = Math.round(size * 0.65);
        const offset = Math.round((size - iconSize) / 2);
        return `<div style="width: ${size}px; height: ${size}px; border-radius: 50%; background: ${escapeHtml(colour)}; position: relative; display: inline-block; vertical-align: middle;">
			<img src="${url}" alt="" style="width: ${iconSize}px; height: ${iconSize}px; object-fit: contain; position: absolute; left: ${offset}px; top: ${offset}px; filter: brightness(0) invert(1);" />
		</div>`;
    }
    function merchantIconHtml(m, resolvedUrl, isLogo, size = 32) {
        if (isLogo) {
            return `<img src="${resolvedUrl}" alt="" crossorigin="anonymous" referrerpolicy="no-referrer"
				style="width: ${size}px; height: ${size}px; border-radius: 50%; background: #E7ECF7; object-fit: cover; vertical-align: middle; flex-shrink: 0;" />`;
        }
        if (!wkOnly && m.isIncome) {
            return `<img src="${resolvedUrl}" alt="" style="width: ${size}px; height: ${size}px; border-radius: 50%; background: #E7ECF7; vertical-align: middle; flex-shrink: 0;" />`;
        }
        const iconSize = Math.round(size * 0.65);
        const offset = Math.round((size - iconSize) / 2);
        return `<div style="width: ${size}px; height: ${size}px; border-radius: 50%; background: ${escapeHtml(m.iconColour ?? m.colour)}; position: relative; display: inline-block; vertical-align: middle;">
			<img src="${resolvedUrl}" alt="" crossorigin="anonymous" style="width: ${iconSize}px; height: ${iconSize}px; object-fit: contain; position: absolute; left: ${offset}px; top: ${offset}px; filter: brightness(0) invert(1);" />
		</div>`;
    }
    function categoryPillHtml(label, colour) {
        if (!label)
            return '';
        return `<div style="display: inline-block; font-size: 10px; line-height: 14px; color: #2A2540; padding: 3px 10px 3px 8px; border-radius: 100px; background: #EFEAE2; margin-top: 4px;">
			<span style="display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: ${escapeHtml(colour)}; margin-right: 5px; vertical-align: middle;"></span><span style="vertical-align: middle;">${escapeHtml(label)}</span>
		</div>`;
    }
    const html = `
		<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; color: #2A2540; padding: 40px; width: 794px; box-sizing: border-box; background: #FFFFFF;">
			<div style="display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 3px solid #413655; padding-bottom: 18px; margin-bottom: 28px;">
				<div>
					<div style="font-size: 13px; letter-spacing: 2px; color: #8579A0; text-transform: uppercase; margin-bottom: 6px;">Money Guided</div>
					<div style="font-size: 32px; font-weight: 700; color: #413655; line-height: 1.1;">Spending Analysis</div>
					<div style="font-size: 16px; color: #5A4F73; margin-top: 6px;">${escapeHtml(range)}</div>
				</div>
				<div style="text-align: right; font-size: 12px; color: #8579A0;">
					<div>Generated</div>
					<div style="font-weight: 600; color: #413655; margin-top: 2px;">${escapeHtml(generated)}</div>
				</div>
			</div>

			<div style="display: flex; gap: 12px; margin-bottom: 28px;">
				<div style="flex: 1; background: #F4F1EC; border-radius: 12px; padding: 18px;">
					<div style="font-size: 11px; color: #8579A0; letter-spacing: 1px; text-transform: uppercase;">Money in</div>
					<div style="font-size: 24px; font-weight: 700; color: #2E8B6F; margin-top: 6px;">${escapeHtml(fmtMoney(totalIn, formatter))}</div>
				</div>
				<div style="flex: 1; background: #F4F1EC; border-radius: 12px; padding: 18px;">
					<div style="font-size: 11px; color: #8579A0; letter-spacing: 1px; text-transform: uppercase;">Money out</div>
					<div style="font-size: 24px; font-weight: 700; color: #B85C5C; margin-top: 6px;">${escapeHtml(fmtMoney(totalOut, formatter))}</div>
				</div>
				<div style="flex: 1; background: ${net >= 0 ? '#E6F2EC' : '#FBEAEA'}; border-radius: 12px; padding: 18px;">
					<div style="font-size: 11px; color: #8579A0; letter-spacing: 1px; text-transform: uppercase;">Net</div>
					<div style="font-size: 24px; font-weight: 700; color: ${net >= 0 ? '#2E8B6F' : '#B85C5C'}; margin-top: 6px;">${escapeHtml(fmtMoney(net, formatter))}</div>
				</div>
			</div>

			${breakdown.length ? `
			<div style="margin-bottom: 28px;">
				<div style="font-size: 18px; font-weight: 700; color: #413655; margin-bottom: 14px;">Category breakdown</div>
				${breakdown.map((b) => `
					<div style="margin-bottom: 12px;">
						<div style="display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 4px;">
							<span style="font-weight: 600; color: #413655;">${escapeHtml(b.name)}</span>
							<span style="color: #5A4F73;">${escapeHtml(fmtMoney(b.amount, formatter))}</span>
						</div>
						<div style="background: #EFEAE2; height: 10px; border-radius: 6px; overflow: hidden;">
							<div style="background: ${escapeHtml(b.colour)}; width: ${(b.amount / maxBreakdown * 100).toFixed(1)}%; height: 100%;"></div>
						</div>
					</div>
				`).join('')}
			</div>` : ''}

			${slices.length ? `
			<div style="margin-bottom: 28px;">
				<div style="font-size: 18px; font-weight: 700; color: #413655; margin-bottom: 14px;">${slices[0]?.week ? 'Weekly' : 'Monthly'} spend</div>
				<table style="width: 100%; border-collapse: collapse; border-bottom: 1px solid #EFEAE2;">
					<tr style="vertical-align: bottom; height: 120px;">
						${slices.map((s) => {
        const v = Math.abs(s.totalOut ?? 0) || Math.abs(s.net ?? 0);
        const h = Math.max(2, Math.round(v / sliceMax * 110));
        return `<td style="padding: 0 3px; vertical-align: bottom;">
								<div style="background: #413655; height: ${h}px; border-radius: 4px 4px 0 0;"></div>
							</td>`;
    }).join('')}
					</tr>
					<tr>
						${slices.map((s) => `<td style="padding: 6px 3px 0; text-align: center; font-size: 9px; color: #8579A0;">${escapeHtml(s.label ?? '')}</td>`).join('')}
					</tr>
					<tr>
						${slices.map((s) => {
        const v = Math.abs(s.totalOut ?? 0) || Math.abs(s.net ?? 0);
        return `<td style="padding: 2px 3px 0; text-align: center; font-size: 9px; color: #413655; font-weight: 600;">${escapeHtml(fmtMoney(v, formatter))}</td>`;
    }).join('')}
					</tr>
				</table>
			</div>` : ''}

			${topCategories.length ? `
			<div style="margin-bottom: 28px;">
				<div style="font-size: 18px; font-weight: 700; color: #413655; margin-bottom: 14px;">Top sub-categories</div>
				<table style="width: 100%; border-collapse: collapse; font-size: 13px;">
					<tbody>
						${topCategories.map((c) => `
							<tr style="border-bottom: 1px solid #F4F1EC;">
								<td style="padding: 10px 4px; width: 44px; vertical-align: middle;">
									${categoryIconHtml(c.iconPath, c.colour, 32)}
								</td>
								<td style="padding: 10px 8px; vertical-align: middle;">
									<div style="color: #413655; font-weight: 600;">${escapeHtml(c.name ?? '')}</div>
									${categoryPillHtml(c.parent ?? '', c.colour)}
								</td>
								<td style="padding: 10px 4px; text-align: right; color: #413655; font-weight: 600; vertical-align: middle; white-space: nowrap;">${escapeHtml(fmtMoney(c.amount, formatter))}</td>
							</tr>
						`).join('')}
					</tbody>
				</table>
			</div>` : ''}

			${topMerchants.length ? `
			<div style="margin-bottom: 28px;">
				<div style="font-size: 18px; font-weight: 700; color: #413655; margin-bottom: 14px;">Top merchants</div>
				<table style="width: 100%; border-collapse: collapse; font-size: 13px;">
					<tbody>
						${topMerchants.map((m, i) => `
							<tr style="border-bottom: 1px solid #F4F1EC;">
								<td style="padding: 10px 4px; width: 44px; vertical-align: middle;">
									${merchantIconHtml(m, resolvedMerchantIcons[i].url, resolvedMerchantIcons[i].isLogo, 32)}
								</td>
								<td style="padding: 10px 8px; vertical-align: middle;">
									<div style="color: #413655; font-weight: 600;">${escapeHtml(m.name)}</div>
									${categoryPillHtml(m.subCategory ?? '', m.iconColour ?? m.colour)}
								</td>
								<td style="padding: 10px 8px; text-align: right; color: #5A4F73; vertical-align: middle; white-space: nowrap;">${m.count} ${m.count === 1 ? 'tx' : 'txs'}</td>
								<td style="padding: 10px 4px; text-align: right; color: ${m.isIncome ? '#2E8B6F' : '#413655'}; font-weight: 600; vertical-align: middle; white-space: nowrap;">${m.isIncome ? '+' : ''}${escapeHtml(fmtMoney(m.amount, formatter))}</td>
							</tr>
						`).join('')}
					</tbody>
				</table>
			</div>` : ''}

			${recurring.length ? `
			<div style="margin-bottom: 28px;">
				<div style="font-size: 18px; font-weight: 700; color: #413655; margin-bottom: 14px;">Recurring payments (${recurring.length})</div>
				<table style="width: 100%; border-collapse: collapse; font-size: 13px;">
					<tbody>
						${recurring.slice(0, 15).map((p) => `
							<tr style="border-bottom: 1px solid #F4F1EC;">
								<td style="padding: 8px 4px; color: #413655; font-weight: 600;">${escapeHtml(p.merchantName ?? p.name ?? 'Payment')}</td>
								<td style="padding: 8px 4px; text-align: right; color: #413655; font-weight: 600;">${escapeHtml(fmtMoney(Math.abs(p.amount ?? p.averageAmount ?? 0), formatter))}</td>
							</tr>
						`).join('')}
					</tbody>
				</table>
			</div>` : ''}

			<div style="margin-top: 40px; padding-top: 16px; border-top: 1px solid #EFEAE2; font-size: 10px; color: #8579A0; text-align: center;">
				Generated by Money Guided · Spending analysis is informational and does not constitute financial advice.
			</div>
		</div>
	`;
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.innerHTML = html;
    document.body.appendChild(container);
    try {
        const html2pdf = (await import('html2pdf.js')).default;
        const opts = {
            margin: 0,
            filename: `spending-analysis-${(range || 'report').replace(/\s+/g, '-').toLowerCase()}.pdf`,
            image: { type: 'jpeg', quality: 0.95 },
            html2canvas: { scale: 2, useCORS: true, backgroundColor: '#FFFFFF' },
            jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        };
        await html2pdf().set(opts).from(container.firstElementChild).save();
    }
    finally {
        document.body.removeChild(container);
    }
}
